import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Bell,
    BookOpen,
    ChevronRight,
    Check,
    Clock,
    Flame,
    Mic,
    PenLine,
    PlusCircle,
    Sparkles,
    Star,
    Target,
} from "lucide-react";

import { slateCardClass, primaryCtaClass } from "@/lib/card-styles";
import { cn } from "@/lib/utils";
import { typeMetaClass, typeNumericClass } from "@/lib/typography";
import { estimateSessionMinutes } from "@/lib/session-metadata";
import { useLanguage } from "@/i18n";
import { useGuardedNavigate } from "@/hooks/use-guarded-navigate";
import { useGamificationUIStore } from "@lyder-talk/core";
import type { StreakCalendarDay } from "@/hooks/use-gamification";
import {
    useDailyChallenges,
    type ChallengeType,
} from "@/hooks/use-daily-challenge";

interface TodayStripProps {
    /** Words waiting on spaced repetition. */
    dueForReview: number;
    currentStreak: number;
    /** XP earned this week, against `weeklyGoal`. */
    weeklyXp: number;
    weeklyGoal?: number;
    /** Optional Mon–Sun activity rows for the 7-day study rhythm dots. */
    weeklyCalendar?: StreakCalendarDay[];
    className?: string;
}

/** Where each challenge type sends the learner to make progress on it. */
const CHALLENGE_ROUTES: Record<ChallengeType, string> = {
    vocabulary_review: "/vocabulary/practice",
    practice_topic: "/topics",
    create_topic: "/my-topics",
    study_time: "/topics",
    video_shadowing: "/videos",
    video_dictation: "/videos",
};

const CHALLENGE_ICONS: Record<ChallengeType, typeof Star> = {
    vocabulary_review: BookOpen,
    practice_topic: Mic,
    create_topic: PlusCircle,
    study_time: Clock,
    video_shadowing: Mic,
    video_dictation: PenLine,
};

const DAY_INITIALS = ["M", "T", "W", "T", "F", "S", "S"];

/**
 * The one context band on the home page.
 *
 * Calm focus allows a single strip between the task header and the content,
 * so this deliberately merges what used to be separate widgets: the review
 * reminder, the streak (with a 7-day Mon–Sun study rhythm), the weekly-XP bar
 * — and the day's challenges. Whichever number points at an action gets the
 * CTA; the rest are quiet supporting counts.
 */
const TodayStrip = ({
    dueForReview,
    currentStreak,
    weeklyXp,
    weeklyGoal = 500,
    weeklyCalendar,
    className,
}: TodayStripProps) => {
    const guardedNavigate = useGuardedNavigate();
    const { t } = useTranslation();
    const languageState = useLanguage();
    const { challenges, isLoading: challengesLoading } = useDailyChallenges();
    const { setStreakModalOpen, setStreakModalTab } = useGamificationUIStore();
    const completedCount = challenges.filter((c) => c.is_completed).length;
    const formatNumber =
        languageState?.format.number ?? ((n: number) => n.toLocaleString());

    /** Phone CTA: the full challenge list lives in the streak dialog. */
    const openChallenges = () => {
        setStreakModalTab("challenges");
        setStreakModalOpen(true);
    };

    const hasDue = dueForReview > 0;
    const isNewStreak = currentStreak === 0;
    const reviewMinutes = estimateSessionMinutes(dueForReview);
    const goalMet = weeklyXp >= weeklyGoal && weeklyGoal > 0;
    const xpPercent = Math.min(100, Math.round((weeklyXp / weeklyGoal) * 100));

    // Exactly one primary action, chosen by what is most actionable.
    const action = hasDue
        ? {
              label: t("home.today.ctaReview", { count: dueForReview }),
              hint: reviewMinutes
                  ? t("home.today.aboutMinutes", { count: reviewMinutes })
                  : undefined,
              onClick: () => void guardedNavigate("/vocabulary/practice"),
          }
        : {
              label: isNewStreak
                  ? t("home.today.ctaStartStreak", "Start today's practice")
                  : t("home.today.ctaKeepPractising", "Keep practising"),
              hint: isNewStreak
                  ? t("home.today.startStreakHint", "Start a streak that sticks")
                  : undefined,
              onClick: () => void guardedNavigate("/my-topics"),
          };

    const todayIso = new Date().toISOString().split("T")[0];

    return (
        <section
            aria-label={t("home.today.label", "Today")}
            data-tour="streak-level-widget"
            className={cn(
                slateCardClass,
                "relative overflow-hidden p-3.5 pl-4 sm:p-5 sm:pl-6",
                hasDue &&
                    "border-amber-200/80 bg-gradient-to-r from-amber-50/70 via-amber-50/30 to-transparent dark:border-amber-900/60 dark:from-amber-950/25 dark:via-amber-950/10 dark:to-transparent",
                !hasDue &&
                    "bg-gradient-to-r from-stone-50/80 via-background to-transparent dark:from-stone-900/40 dark:via-background dark:to-transparent",
                className
            )}
        >
            {/* A single amber rail marks the strip as actionable. */}
            <span
                aria-hidden
                className={cn(
                    "absolute inset-y-0 left-0 w-1",
                    hasDue ? "bg-amber-500" : "bg-stone-300 dark:bg-stone-700"
                )}
            />

            <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-6 gap-y-3">
                    {hasDue && (
                        <div
                            data-tour="vocabulary-widget"
                            className="flex items-center gap-2 sm:gap-2.5"
                        >
                            <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-amber-500/15 text-amber-600 sm:h-9 sm:w-9 dark:text-amber-400">
                                <Bell className="h-4 w-4" aria-hidden="true" />
                            </span>
                            <div>
                                <p className={typeMetaClass}>{t("home.today.dueForReview", "Due for review")}</p>
                                <p className={cn(typeNumericClass, "text-base font-bold leading-tight sm:text-lg")}>
                                    {dueForReview}
                                    <span className="ml-1 font-sans text-xs font-normal text-stone-500 dark:text-stone-400">
                                        {t("home.today.wordsReady", { count: dueForReview })}
                                    </span>
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="flex items-center gap-2 sm:gap-2.5">
                        <span
                            className={cn(
                                "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl sm:h-9 sm:w-9",
                                currentStreak > 0
                                    ? "bg-orange-500/15 text-orange-600 dark:text-orange-400"
                                    : "bg-stone-100 text-stone-500 dark:bg-stone-800 dark:text-stone-400"
                            )}
                        >
                            <Flame className="h-4 w-4" aria-hidden="true" />
                        </span>
                        <div>
                            <p className={typeMetaClass}>{t("home.today.streak", "Streak")}</p>
                            <div className="flex items-center gap-2.5">
                                <p className={cn(typeNumericClass, "text-base font-bold leading-tight sm:text-lg")}>
                                    {currentStreak}
                                    <span className="ml-1 font-sans text-xs font-normal text-stone-500 dark:text-stone-400">
                                        {t("home.today.day", { count: currentStreak })}
                                    </span>
                                </p>
                                {/* Compact Mon–Sun rhythm dots on desktop */}
                                {weeklyCalendar && weeklyCalendar.length === 7 && (
                                    <div
                                        aria-hidden="true"
                                        className="hidden items-center gap-1 lg:inline-flex"
                                    >
                                        {weeklyCalendar.map((day, idx) => {
                                            const active =
                                                day.practice_sessions > 0 ||
                                                day.xp_earned > 0 ||
                                                day.challenges_completed > 0;
                                            const isToday = day.activity_date === todayIso;
                                            return (
                                                <span
                                                    key={day.activity_date}
                                                    title={`${DAY_INITIALS[idx]}: ${day.xp_earned} XP`}
                                                    className={cn(
                                                        "inline-flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-bold transition-colors",
                                                        active
                                                            ? "bg-orange-500/20 text-orange-700 ring-1 ring-orange-500/40 dark:bg-orange-500/25 dark:text-orange-300"
                                                            : isToday
                                                              ? "border border-dashed border-stone-400 text-stone-500 dark:border-stone-600 dark:text-stone-400"
                                                              : "bg-stone-200/70 text-stone-400 dark:bg-stone-800 dark:text-stone-500"
                                                    )}
                                                >
                                                    {DAY_INITIALS[idx]}
                                                </span>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="min-w-[8rem] flex-1">
                        <div className="mb-1 flex items-center justify-between gap-2">
                            <span className={cn(typeMetaClass, "inline-flex items-center gap-1.5")}>
                                <Sparkles className="h-3 w-3" aria-hidden="true" />
                                {t("home.today.weeklyXp", "Weekly XP")}
                            </span>
                            <span className="inline-flex items-center gap-1.5 text-[11px] tabular-nums text-stone-500 dark:text-stone-400">
                                {/* Past the goal "1,100/500" reads like a
                                    broken meter next to its own check; the
                                    number alone plus the check is the honest
                                    version. */}
                                <span>
                                    {goalMet
                                        ? formatNumber(weeklyXp)
                                        : `${formatNumber(weeklyXp)}/${formatNumber(weeklyGoal)}`}
                                </span>
                                {goalMet && (
                                    <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-500/15 px-1.5 py-px text-[9px] font-bold uppercase tracking-wider text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300">
                                        <Check className="h-2.5 w-2.5" aria-hidden="true" />
                                    </span>
                                )}
                            </span>
                        </div>
                        <div
                            className={cn(
                                "h-1.5 w-full overflow-hidden rounded-full",
                                goalMet
                                    ? "bg-emerald-100 dark:bg-emerald-950/50"
                                    : "bg-orange-100 dark:bg-orange-950/50"
                            )}
                            role="progressbar"
                            aria-valuenow={weeklyXp}
                            aria-valuemin={0}
                            aria-valuemax={weeklyGoal}
                            aria-label={t("home.today.weeklyXpAria", "Weekly XP progress")}
                        >
                            <div
                                className={cn(
                                    "h-full rounded-full transition-all duration-500",
                                    goalMet
                                        ? "bg-gradient-to-r from-emerald-500 to-teal-500"
                                        : "bg-gradient-to-r from-orange-500 to-amber-500"
                                )}
                                style={{ width: `${xpPercent}%` }}
                            />
                        </div>
                    </div>
                </div>

                {/* Below `lg` the CTA gets its own full-width row: at tablet
                    width a fixed 208px column squeezed the three stats into a
                    stack of two and left them uneven beside it. */}
                <div className="w-full lg:w-52">
                    <button
                        type="button"
                        onClick={action.onClick}
                        className={cn(primaryCtaClass, "h-9 justify-center")}
                    >
                        {action.label}
                    </button>
                    {action.hint && (
                        <p className="mt-1 text-center text-[11px] text-stone-500 dark:text-stone-400">
                            {action.hint}
                        </p>
                    )}
                </div>
            </div>

            {/* The day's challenges. Phones get one button: six chips wrap
                into three rows there, which is what pushed the resume list
                under the fold. Tablet and up keep the rail, where every
                challenge is one tap away without opening anything. */}
            {(challengesLoading || challenges.length > 0) && (
                <div className="mt-3 border-t border-stone-200/60 pt-2.5 sm:mt-3.5 sm:pt-3 dark:border-stone-800/60">
                    <button
                        type="button"
                        onClick={openChallenges}
                        className={cn(
                            "flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2",
                            "text-[11px] font-semibold text-stone-700 transition-colors sm:hidden",
                            "border-stone-200/80 bg-white/80 hover:border-primary/40 hover:bg-white",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                            "dark:border-stone-800 dark:bg-stone-900/70 dark:text-stone-200 dark:hover:border-primary/50",
                        )}
                    >
                        <span className="inline-flex items-center gap-1.5">
                            <Target
                                className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400"
                                aria-hidden="true"
                            />
                            {t("gamification.challenges.title", "Daily challenges")}
                        </span>
                        <span className="inline-flex items-center gap-1 tabular-nums text-stone-500 dark:text-stone-400">
                            {challengesLoading
                                ? "…"
                                : `${completedCount}/${challenges.length}`}
                            <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                        </span>
                    </button>

                    <div className="hidden sm:flex sm:flex-wrap sm:items-center sm:gap-x-2.5 sm:gap-y-2">
                        <span className={cn(typeMetaClass, "shrink-0")}>
                            {t("gamification.challenges.title", "Daily challenges")}
                        </span>

                        {challengesLoading ? (
                            <div className="flex flex-wrap gap-2" aria-hidden="true">
                                {Array.from({ length: 4 }).map((_, i) => (
                                    <span
                                        key={i}
                                        className="h-6 w-32 animate-pulse rounded-full bg-stone-200/70 dark:bg-stone-800"
                                    />
                                ))}
                            </div>
                        ) : (
                            <ul className="flex min-w-0 flex-wrap items-center gap-1.5">
                                {challenges.map((challenge) => {
                                    const Icon =
                                        CHALLENGE_ICONS[challenge.challenge_type] ?? Star;
                                    const done = challenge.is_completed;
                                    const label = t(
                                        `gamification.challenges.${challenge.challenge_type}`,
                                        {
                                            defaultValue: t(
                                                "gamification.challenges.default",
                                                "Daily challenge",
                                            ),
                                        },
                                    );

                                    return (
                                        <li key={challenge.id}>
                                            <Link
                                                to={CHALLENGE_ROUTES[challenge.challenge_type] ?? "/"}
                                                className={cn(
                                                    "group inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1",
                                                    "text-[11px] font-semibold transition-colors",
                                                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                                                    done
                                                        ? "border-emerald-200/80 bg-emerald-50/70 text-emerald-800 hover:border-emerald-300 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-300"
                                                        : "border-stone-200 bg-white/80 text-stone-700 hover:border-primary/40 hover:bg-white dark:border-stone-800 dark:bg-stone-900/70 dark:text-stone-200 dark:hover:border-primary/50"
                                                )}
                                            >
                                                {done ? (
                                                    <Check className="h-3 w-3 shrink-0" aria-hidden="true" />
                                                ) : (
                                                    <Icon className="h-3 w-3 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden="true" />
                                                )}
                                                <span className="whitespace-nowrap">{label}</span>
                                                <span className="shrink-0 tabular-nums text-[10px] font-bold text-stone-500 dark:text-stone-400">
                                                    {challenge.current_count}/{challenge.target_count}
                                                    {challenge.challenge_type === "study_time" &&
                                                        ` ${t("gamification.challenges.minutesShort", "min")}`}
                                                </span>
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
};

export { TodayStrip };
export type { TodayStripProps };
